import { FlexColumn, FlexRow, MaterialIcon, StyledButton, useLoading, useStyles } from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { ButtonGroup } from 'baseui/button-group';
import { Input } from 'baseui/input';
import { Tab, TabOverrides, Tabs } from 'baseui/tabs-motion';
import { LabelSmall } from 'baseui/typography';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Rnd } from 'react-rnd';
import { useRecoilState, useRecoilValue, useResetRecoilState } from 'recoil';
import { useWindowSize } from 'usehooks-ts';
import {
  TIMELINE_WINDOW_HEIGHT,
  VIDEO_REQUEST_WINDOW_HEIGHT,
  VIDEO_REQUEST_WINDOW_WIDTH,
} from '../../../constants/app';
import { CAPTURE_DURATION } from '../../../constants/streaming';
import { useFeedback } from '../../../hooks/useFeedback';
import { getStreamingVideoCapture } from '../../../services/streamingService';
import { floatingCardsXPositionState, sidenavWidthState } from '../../../storage/app';
import {
  capturingCurrentStateState,
  capturingMiddleDateState,
  capturingState,
  capturingTimelineDateBracketsState,
  capturingTimelineManualDateState,
  capturingTimelineMapEventState,
  playbackStreamingState,
  timelineCanRequestVideoState,
  timelineEvidenceEventsState,
  timelineSelectedRecordState,
} from '../../../storage/streaming';
import { ChannelId } from '../../../types/gptTypes';
import { CaptureMode, StreamType } from '../../../types/streaming';
import { hasEnabledCameraFeatures } from '../../../utils/currentStates';
import CapturingVideoTimeline from './capturingVideo/CapturingVideoTimeline';
import CapturingVideoEvidences from './CapturingVideoEvidences';
import FloatingPlaybackWindow from './shared/FloatingPlaybackWindow';

function CapturingVideo() {
  const { css, theme } = useStyles();
  const { loading, startLoading, stopLoading } = useLoading();
  const { showFailFeedback, showPositiveFeedback } = useFeedback();
  const { width, height } = useWindowSize();
  const [capturingData, setCapturingData] = useRecoilState(capturingState);
  const [channel, setChannel] = useState<ChannelId>(ChannelId.front);
  const [manualDate, setManualDate] = useRecoilState(capturingTimelineManualDateState);
  const [playbackStreaming, setPlaybackStreaming] = useRecoilState(playbackStreamingState);
  const duration = playbackStreaming.duration;
  const resetPlaybackStreaming = useResetRecoilState(playbackStreamingState);
  const { selectedDate, captureMode } = capturingData;
  const isPlaybackMode = captureMode === CaptureMode.playback;
  const timelineMetadata = {
    streamType: isPlaybackMode ? StreamType.main : undefined,
    channel: !isPlaybackMode ? channel : undefined,
  };
  const canRequest = useRecoilValue(timelineCanRequestVideoState(timelineMetadata));
  const timeBrackets = useRecoilValue(capturingTimelineDateBracketsState(timelineMetadata));
  const xPosition = useRecoilValue(floatingCardsXPositionState);
  const sidenavWidth = useRecoilValue(sidenavWidthState(false));
  const initialWindowWidth = width - xPosition - sidenavWidth - 94;
  const windowYPosition = height - TIMELINE_WINDOW_HEIGHT - 60 - 26;
  const currentState = useRecoilValue(capturingCurrentStateState);
  const selectedRecord = useRecoilValue(timelineSelectedRecordState);
  const selectedMapEvent = useRecoilValue(capturingTimelineMapEventState);
  const events = useRecoilValue(timelineEvidenceEventsState);
  const requestingYPosition = height - TIMELINE_WINDOW_HEIGHT - 60 - 26 - 12 - VIDEO_REQUEST_WINDOW_HEIGHT;
  const dateRange = {
    start: capturingData.startDate,
    end: capturingData.endDate,
  };
  const enabledCameraFeatures = hasEnabledCameraFeatures(currentState);
  const middleDate = useRecoilValue(capturingMiddleDateState);
  const canPlayback = enabledCameraFeatures && isPlaybackMode;
  const disabledControls = isPlaybackMode && !canPlayback;

  const tabOverrides: TabOverrides = {
    Tab: {
      style: {
        paddingTop: theme.sizing.scale400,
        paddingRight: theme.sizing.scale600,
        paddingBottom: theme.sizing.scale400,
        paddingLeft: theme.sizing.scale600,
      },
    },
  };

  useEffect(() => {
    return () => {
      resetPlaybackStreaming();
    };
  }, []);

  useEffect(() => {
    return () => {
      setPlaybackStreaming((prev) => ({
        ...prev,
        ident: undefined,
        startDate: undefined,
        internalStuck: false,
        frontStuck: false,
      }));
    };
  }, [middleDate]);

  const setMode = (mode: CaptureMode) => {
    setCapturingData((prev) => ({ ...prev, captureMode: mode }));
  };

  const onRequestVideo = async () => {
    if (!currentState?.asset._id || !currentState.asset.client?._id || !dateRange.start) return;
    startLoading();
    const { error } = await getStreamingVideoCapture({
      assetId: currentState.asset._id,
      channelId: channel,
      clientId: currentState.asset.client._id,
      startDate: dateRange.start,
      subClientId: currentState.asset.subClient?._id,
      duration: CAPTURE_DURATION[duration],
    });
    if (error) {
      showFailFeedback('No se pudo completar la solicitud de video, intenta más tarde.');
    } else {
      showPositiveFeedback('Se ha solicitado el video, lo puedes encontrar en el reporte de historial de evidencias.');
    }
    stopLoading();
  };

  const onPlaybackVideo = async () => {
    setPlaybackStreaming((prev) => ({
      ...prev,
      ident: capturingData.ident,
      startDate: middleDate,
    }));
  };

  const selectedChannelIndex = (() => {
    if (channel === ChannelId.dome) return 2;
    if (channel === ChannelId.internal) return 1;
    return 0;
  })();

  const onSelectedChannelChange = (_event: React.SyntheticEvent<HTMLButtonElement, Event>, index: number) => {
    if (index === 2) {
      setChannel(ChannelId.dome);
      return;
    }
    if (index === 1) {
      setChannel(ChannelId.internal);
      return;
    }
    setChannel(ChannelId.front);
  };

  const formatTimeFromTs = (ts?: number) => {
    if (!ts) return undefined;
    return moment(ts).format('HH:mm:ss');
  };

  const SharedControls = () => {
    return (
      <>
        <FlexColumn gap={theme.sizing.scale200}>
          <LabelSmall>Indica la duración:</LabelSmall>
          <ButtonGroup
            size="mini"
            mode="radio"
            selected={disabledControls ? undefined : duration}
            onClick={(_event, index) => {
              setPlaybackStreaming({
                ident: undefined,
                startDate: undefined,
                internalStuck: false,
                frontStuck: false,
                duration: index,
              });
            }}
            disabled={disabledControls}
          >
            <Button>5s</Button>
            <Button>10s</Button>
            <Button>30s</Button>
            <Button>1min</Button>
            <Button>5min</Button>
          </ButtonGroup>
        </FlexColumn>
        <FlexColumn gap={theme.sizing.scale200}>
          <LabelSmall>Hora inicio:</LabelSmall>
          <Input
            type="time"
            size="compact"
            step={1}
            disabled={disabledControls}
            value={formatTimeFromTs(captureMode === CaptureMode.playback ? middleDate : dateRange.start)}
            onChange={({ target: { value } }) => {
              if (value) {
                const [hours, minutes, seconds] = value.split(':');
                setManualDate(
                  moment(captureMode === CaptureMode.playback ? middleDate : dateRange.start)
                    .hour(+hours)
                    .minutes(+minutes)
                    .seconds(+seconds)
                    .valueOf(),
                );
              }
            }}
            required
            overrides={{
              Root: {
                style: {
                  width: '150px',
                },
              },
              Input: {
                style: {
                  paddingTop: '3px',
                  paddingRight: theme.sizing.scale300,
                  paddingBottom: '3px',
                  paddingLeft: theme.sizing.scale300,
                },
              },
            }}
          />
        </FlexColumn>
      </>
    );
  };

  if (!currentState) {
    return null;
  }

  return (
    <>
      <Rnd
        default={{
          x: xPosition,
          y: windowYPosition,
          width: initialWindowWidth,
          height: TIMELINE_WINDOW_HEIGHT,
        }}
        minWidth={initialWindowWidth}
        className={css({
          backgroundColor: '#fff',
          border: '1px solid #E2E2E2',
          borderRadius: theme.sizing.scale500,
          display: 'flex',
          overflow: 'hidden',
          padding: theme.sizing.scale600,
          width: '100%',
        })}
        enableResizing={false}
        bounds="body"
        disableDragging
        position={{
          x: xPosition,
          y: windowYPosition,
        }}
        size={{
          height: TIMELINE_WINDOW_HEIGHT,
          width: initialWindowWidth,
        }}
      >
        <CapturingVideoTimeline
          onChange={({ end, start }) => {
            setCapturingData((prev) => ({ ...prev, startDate: start, endDate: end }));
          }}
          mode={captureMode}
          duration={duration}
          day={selectedDate}
          availableTracks={timeBrackets?.map((bracket) => ({
            startDate: bracket.begin,
            endDate: bracket.end,
          }))}
          endDate={selectedRecord?.endTime}
          startDate={selectedRecord?.startTime}
          selectedEventDate={selectedMapEvent?.created}
          selectedDate={manualDate}
          events={events}
        />
      </Rnd>
      <Rnd
        default={{
          x: xPosition + 12,
          y: requestingYPosition,
          width: VIDEO_REQUEST_WINDOW_WIDTH,
          height: VIDEO_REQUEST_WINDOW_HEIGHT,
        }}
        className={css({
          backgroundColor: '#fff',
          border: '1px solid #E2E2E2',
          borderRadius: theme.sizing.scale500,
          display: 'flex !important',
          overflow: 'hidden',
          padding: `${theme.sizing.scale300} ${theme.sizing.scale300} 0`,
          width: '100%',
        })}
        enableResizing={false}
        bounds="body"
        disableDragging
        position={{
          x: xPosition + 12,
          y: requestingYPosition,
        }}
        size={{
          width: VIDEO_REQUEST_WINDOW_WIDTH,
          height: VIDEO_REQUEST_WINDOW_HEIGHT,
        }}
      >
        <Tabs
          activeKey={captureMode}
          onChange={({ activeKey }) => {
            if (activeKey === '1') {
              setMode(CaptureMode.requesting);
            }
            if (activeKey === '2') {
              setMode(CaptureMode.playback);
            }
          }}
          overrides={{
            Root: {
              style: {
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
              },
            },
          }}
        >
          <Tab
            key={CaptureMode.playback}
            title="Reproducir"
            overrides={tabOverrides}
          >
            <FlexColumn
              gap={theme.sizing.scale400}
              classNames={css({ height: '100%' })}
            >
              <SharedControls />
              <FlexRow
                gap={theme.sizing.scale300}
                classNames={css({ marginTop: 'auto' })}
              >
                <StyledButton
                  size="compact"
                  isLoading={loading || !timeBrackets}
                  onClick={() => {
                    onPlaybackVideo();
                  }}
                  startEnhancer={
                    <MaterialIcon
                      name={disabledControls || !canRequest ? 'videocam_off' : 'videocam'}
                      color={
                        disabledControls || !canRequest
                          ? theme.colors.buttonDisabledText
                          : theme.colors.contentInversePrimary
                      }
                      size="16px"
                    />
                  }
                  full
                  disabled={disabledControls || !canRequest}
                >
                  {disabledControls ? 'Fuera de línea' : 'Solicitar reproducción'}
                </StyledButton>
              </FlexRow>
            </FlexColumn>
          </Tab>
          <Tab
            key={CaptureMode.requesting}
            title="Descargar"
            overrides={tabOverrides}
          >
            <FlexColumn
              gap={theme.sizing.scale400}
              classNames={css({ height: '100%' })}
            >
              <FlexColumn gap={theme.sizing.scale100}>
                <LabelSmall>Selecciona una fuente:</LabelSmall>
                <ButtonGroup
                  size="mini"
                  mode="radio"
                  selected={disabledControls ? undefined : selectedChannelIndex}
                  onClick={onSelectedChannelChange}
                  disabled={disabledControls}
                >
                  <Button>Frontal</Button>
                  <Button>Interna</Button>
                  {currentState.asset.channels === 3 && <Button>Domo</Button>}
                </ButtonGroup>
              </FlexColumn>
              <SharedControls />
              <FlexRow classNames={css({ marginTop: 'auto' })}>
                <StyledButton
                  size="compact"
                  isLoading={loading || !timeBrackets}
                  onClick={() => {
                    onRequestVideo();
                  }}
                  disabled={!canRequest}
                  startEnhancer={
                    <MaterialIcon
                      name="download"
                      color={canRequest ? theme.colors.contentInversePrimary : theme.colors.contentStateDisabled}
                      size="16px"
                    />
                  }
                  full
                >
                  Solicitar descarga
                </StyledButton>
              </FlexRow>
            </FlexColumn>
          </Tab>
        </Tabs>
      </Rnd>
      <CapturingVideoEvidences />
      <FloatingPlaybackWindow duration={duration} />
    </>
  );
}

export default CapturingVideo;
